import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

/**
 * Interface representing an operator in the autocomplete dropdown
 * Defines the structure of each operator option
 */
type RgaOption = string;

/**
 * Props interface for the RgaAutocomplete component
 * Extends the base autocomplete functionality with operator-specific features
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected operator option
 * @property onChange - Callback fired when selection changes
 */
interface RgaAutocompleteProps {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    value?: RgaOption | null;
    onChange?: (event: React.SyntheticEvent, value: RgaOption | null) => void;

    [key: string]: any;
}

/**
 * Interface for the operator search API response
 * Defines the structure of the search response data
 *
 * @property data - Response data container
 * @property resultFlag - Indicates if the search was successful
 */
interface RgaSearchResponse {
    data?: Array<{
        rga?: string;
        submitDateTime?: string;
    }>;
    resultFlag?: boolean;
}

/**
 * RgaAutocomplete Component
 *
 * A searchable dropdown component for selecting operators with the following features:
 * - Smart search that detects email, phone, or name input
 * - Async option loading with debouncing
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * Search behavior:
 * - If input contains '@', searches by email
 * - If input contains numbers, searches by phone
 * - Otherwise, searches by name
 *
 * @component
 */
const RgaAutocomplete: React.FC<RgaAutocompleteProps> = ({
                                                             textFieldProps = {},
                                                             form = false,
                                                             name = undefined,
                                                             required = false,
                                                             setLoadingOptions: setLoadingOptionsProps,
                                                             ...props
                                                         }) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches operator options based on search keywords
     * Intelligently determines search type (email/phone/name) based on input
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching operator options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, _keywords?: string) => {
        const keywords = _keywords?.trim() ?? "";
        if (!keywords) {
            setLoadingOptions(false);
            return [];
        }
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.rgaAutocomplete(keywords) as RgaSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.map(e => e.rga ?? "" satisfies RgaOption) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two operator options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: RgaOption, value: RgaOption | null) =>
        option === value, []);

    /**
     * Formats the display label for an operator option
     * Combines first and last name, filtering out empty values
     */
    const getOptionLabel = useCallback((option: RgaOption) =>
        [option ?? ""].filter(Boolean).join(' ') ?? "", []);

    /**
     * Gets the unique key for an operator option
     */
    const getOptionKey = useCallback((option: RgaOption) => option ?? "", []);

    return (
        <AutocompleteBase<RgaOption>
            keywordsBased
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            canSearchEmptyKeywords
            options={[]} // Initial empty array, will be populated by getOptions
            {...props}
        />
    );
};

export default RgaAutocomplete;